import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingDown, TrendingUp, Download, Calendar, BarChart2 } from 'lucide-react';
import { apiService } from '../services/api';
import type { BillingBreakdownResponse, BillingUserResponse } from '../types';
import { formatCurrency } from '../utils';

type GroupBy = 'model' | 'plan' | 'provider';

function BreakdownRow({ label, requests, totalTokens, costUsd, maxCost }: {
  label: string; requests: number; totalTokens: number; costUsd: number; maxCost: number;
}) {
  const pct = maxCost > 0 ? Math.round((costUsd / maxCost) * 100) : 0;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-3 px-4 font-mono text-sm text-gray-800">{label}</td>
      <td className="py-3 px-4 text-right text-sm text-gray-600">{requests.toLocaleString()}</td>
      <td className="py-3 px-4 text-right text-sm text-gray-600">{totalTokens.toLocaleString()}</td>
      <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">{formatCurrency(costUsd)}</td>
      <td className="py-3 px-4 w-36">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
        </div>
      </td>
    </tr>
  );
}

export default function Finance() {
  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState<GroupBy>('model');
  const [breakdown, setBreakdown] = useState<BillingBreakdownResponse | null>(null);
  const [topUsers, setTopUsers] = useState<BillingUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [bd, summary] = await Promise.all([
        apiService.getBillingBreakdown(days, groupBy),
        apiService.getBillingSummary(days),
      ]);
      setBreakdown(bd);
      // top users extracted from byUser field of summary if present
      setTopUsers((summary as any)?.byUser?.slice(0, 10) ?? []);
    } catch {
      setError('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [days, groupBy]);

  useEffect(() => { void load(); }, [load]);

  const exportCsv = () => {
    if (!breakdown) return;
    const rows = [
      ['Label', 'Requests', 'Total Tokens', 'Cost (USD)'],
      ...Object.entries(breakdown.breakdown).map(([k, v]) => [
        k, String(v.requests), String(v.totalTokens), v.costUsd.toFixed(6),
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aegis-finance-${days}d-${groupBy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const entries = breakdown
    ? Object.entries(breakdown.breakdown).sort((a, b) => b[1].costUsd - a[1].costUsd)
    : [];
  const maxCost = entries[0]?.[1].costUsd ?? 0;
  const totalCost = entries.reduce((s, [, v]) => s + v.costUsd, 0);
  const totalReqs = entries.reduce((s, [, v]) => s + v.requests, 0);
  const totalTokens = entries.reduce((s, [, v]) => s + v.totalTokens, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finance</h1>
          <p className="mt-1 text-sm text-gray-500">Cost breakdown and revenue analysis</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!breakdown}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          <Download size={15} /> Export CSV
        </button>
      </div>

      {/* Controls */}
      <div className="flex gap-3 items-center">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar size={15} />
          {(['7', '14', '30', '90'] as const).map(d => (
            <button
              key={d}
              onClick={() => setDays(Number(d))}
              className={`px-3 py-1 rounded-full text-xs font-medium ${days === Number(d) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm ml-4">
          <BarChart2 size={15} className="text-gray-500" />
          <span className="text-gray-500 text-xs">Group by:</span>
          {(['model', 'plan', 'provider'] as GroupBy[]).map(g => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              className={`px-3 py-1 rounded-full text-xs font-medium ${groupBy === g ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: 'Total Cost', value: formatCurrency(totalCost), icon: DollarSign, color: 'text-red-600' },
          { label: 'Total Requests', value: totalReqs.toLocaleString(), icon: TrendingUp, color: 'text-indigo-600' },
          { label: 'Total Tokens', value: totalTokens.toLocaleString(), icon: TrendingDown, color: 'text-orange-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <Icon size={24} className={color} />
            <div>
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Breakdown Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Breakdown by {groupBy}</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-400 text-sm">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">No data for this period</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Label', 'Requests', 'Tokens', 'Cost', 'Share'].map(h => (
                  <th key={h} className={`py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider ${h !== 'Label' ? 'text-right' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(([label, v]) => (
                <BreakdownRow key={label} label={label} maxCost={maxCost} {...v} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top Users */}
      {topUsers.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Top Consumers ({days}d)</h2>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['User', 'Requests', 'Tokens', 'Cost'].map(h => (
                  <th key={h} className={`py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider ${h !== 'User' ? 'text-right' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topUsers.map((u: any) => (
                <tr key={u.userId} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-800">{u.email ?? u.userId}</td>
                  <td className="py-3 px-4 text-right text-sm text-gray-600">{u.requests.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-sm text-gray-600">{u.totalTokens.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-sm font-medium">{formatCurrency(u.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
